/**
 * Utility functions for working with date strings (YYYY-MM-DD format)
 * Camp schedules are date-only (no times), so we work with strings directly
 * to avoid timezone issues. Only parse to Date objects when formatting for display.
 */

/**
 * Parse an ISO date string (YYYY-MM-DD) to a local Date object
 * This is ONLY for display/formatting purposes. For comparisons, use string comparison.
 * 
 * @param dateStr - ISO date string in YYYY-MM-DD format
 * @returns Date object in local timezone, or null if invalid
 */
export function parseDateString(dateStr: string): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  
  // Create date in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day);
}

/**
 * Extract year from an ISO date string (YYYY-MM-DD)
 * @param dateStr - ISO date string in YYYY-MM-DD format
 * @returns Year as number, or null if invalid
 */
export function getYearFromDateString(dateStr: string): number | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  
  const year = parseInt(parts[0], 10);
  return isNaN(year) ? null : year;
}

/**
 * Compare two date strings (YYYY-MM-DD format)
 * @param dateStr1 - First date string
 * @param dateStr2 - Second date string
 * @returns -1 if dateStr1 < dateStr2, 0 if equal, 1 if dateStr1 > dateStr2
 */
export function compareDateStrings(dateStr1: string, dateStr2: string): number {
  if (dateStr1 < dateStr2) return -1;
  if (dateStr1 > dateStr2) return 1;
  return 0;
}

/**
 * Get today's date as an ISO string (YYYY-MM-DD) in local timezone
 * @returns Today's date as YYYY-MM-DD string
 */
export function getTodayDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate days difference between two date strings
 * @param dateStr1 - First date string (YYYY-MM-DD)
 * @param dateStr2 - Second date string (YYYY-MM-DD)
 * @returns Number of days difference (positive if dateStr1 is later)
 */
export function daysBetween(dateStr1: string, dateStr2: string): number {
  const date1 = parseDateString(dateStr1);
  const date2 = parseDateString(dateStr2);
  
  if (!date1 || !date2) return 0;
  
  const diffTime = date1.getTime() - date2.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format a date string for display
 * @param dateStr - ISO date string (YYYY-MM-DD)
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDateString(
  dateStr: string, 
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
): string {
  const date = parseDateString(dateStr);
  if (!date) return dateStr;
  
  return date.toLocaleDateString('en-US', options);
}


